import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Sidenav from "examples/Sidenav";
import routes from "routes";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import { useMaterialUIController, setMiniSidenav, setOpenSide } from "context";
import { useApi } from "lib/api";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { parseExcelPreview, downloadTemplateXlsx } from "./excelPreview";
import {
  generarTablaAmortizacionFlat,
  formatearMoneda,
  formatearFecha,
  validarPrestamoRow,
} from "./amortizacionFlat";

const CLIENTES_PRESTAMOS_TEMPLATE_COLUMNS = [
  "No prest",
  "cedula",
  "Nombres",
  "Apellidos",
  "Direccion",
  "Telefono",
  "Provincia",
  "celular",
  "sexo",
  "estado civil",
  "dependientes",
  "capital",
  "tasa",
  "frecuencia",
  "plazo",
  "fecha de inicio",
  "balance pendiente",
  "atraso",
  "mora",
];

const STEPS = [
  {
    key: "productos",
    label: "Productos",
    description:
      "Cargue un archivo Excel (.xlsx o .xls) con sus productos. La primera fila debe ser la fila de encabezados.",
    hint: "Puede descargar la plantilla para usar exactamente estas columnas: Chasis, marca, modelo, color, costo, precio min, precio.",
    templateColumns: ["Chasis", "marca", "modelo", "color", "costo", "precio min", "precio"],
    templateFileName: "plantilla_productos.xlsx",
  },
  {
    key: "prestamos",
    label: "Préstamos",
    description:
      "Último paso: cargue el archivo de préstamos a importar. La primera fila son los encabezados.",
    hint: "Plantilla unificada clientes+préstamos: No prest, cedula, Nombres, Apellidos, Direccion, Telefono, Provincia, celular, sexo, estado civil, dependientes, capital, tasa, frecuencia, plazo, fecha de inicio, balance pendiente, atraso, mora.",
    templateColumns: CLIENTES_PRESTAMOS_TEMPLATE_COLUMNS,
    templateFileName: "plantilla_clientes_prestamos.xlsx",
  },
];

const AMORT_TABLE_GRID_COLS =
  "minmax(36px, 0.4fr) minmax(0, 1.1fr) repeat(5, minmax(0, 1fr)) minmax(0, 0.75fr)";

const amortTableCellSx = {
  px: 1.5,
  py: 0.75,
  fontSize: "0.8125rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const amortTableRowSx = {
  display: "grid",
  gridTemplateColumns: AMORT_TABLE_GRID_COLS,
  width: "100%",
  boxSizing: "border-box",
  alignItems: "center",
};

function createEmptyStepState() {
  return {
    fileName: null,
    headers: [],
    rows: [],
    rowCount: 0,
    error: null,
    loading: false,
    status: "idle",
    saveSummary: null,
    validationSummary: null,
  };
}

function getRowValueByNames(row, names) {
  if (!row || typeof row !== "object") return "";
  const keys = Object.keys(row).filter((k) => k !== "id");
  for (const wanted of names) {
    const match = keys.find((k) => k.trim().toLowerCase() === wanted.toLowerCase());
    if (match) return String(row[match] ?? "").trim();
  }
  return "";
}

function normalizeHeader(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function headersMatchTemplate(actualHeaders, templateHeaders) {
  if (actualHeaders.length !== templateHeaders.length) return false;
  return templateHeaders.every(
    (expectedHeader, index) => normalizeHeader(actualHeaders[index]) === normalizeHeader(expectedHeader)
  );
}

function ImportacionDatos() {
  const location = useLocation();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const [stepsData, setStepsData] = useState(() => [
    createEmptyStepState(),
    createEmptyStepState(),
  ]);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);
  const [amortModal, setAmortModal] = useState({
    open: false,
    error: null,
    filas: [],
    resumen: null,
  });
  const [savingPrestamo, setSavingPrestamo] = useState(false);
  const [savingProductos, setSavingProductos] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [previewVersion, setPreviewVersion] = useState(0);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [sucursalOptions, setSucursalOptions] = useState([]);
  const [productoOptions, setProductoOptions] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [prestamoSeleccionadoId, setPrestamoSeleccionadoId] = useState(null);
  const [prestamoSeleccionadoRow, setPrestamoSeleccionadoRow] = useState(null);
  const [detalleCapital, setDetalleCapital] = useState("");
  const [detalleInteres, setDetalleInteres] = useState("");
  const [detalleBalancePendiente, setDetalleBalancePendiente] = useState("");
  const fileInputRef = useRef(null);
  const {nombreNegocio,roleWeb} = controller
  const { post } = useApi();


  const current = stepsData[activeStep];
  const meta = STEPS[activeStep];

  useEffect(() => {
    function handleMiniSidenav() {
      setOpenSide(dispatch, true);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const gridColumns = useMemo(() => {
    if (!current.headers.length) return [];
    return current.headers.map((field) => ({
      field,
      headerName: field,
      flex: 1,
      minWidth: 110,
      sortable: false,
      align: "left",
      headerAlign: "left",
    }));
  }, [current.headers]);

  const productosPaso1 = useMemo(
    () =>
      stepsData[0].rows.map((row, index) => {
        const chasis = getRowValueByNames(row, ["chasis"]);
        const marca = getRowValueByNames(row, ["marca"]);
        const modelo = getRowValueByNames(row, ["modelo"]);
        const labelBase = [chasis, marca, modelo].filter(Boolean).join(" - ");
        return {
          id: row.id ?? index,
          label: labelBase || `Producto ${index + 1}`,
          row,
        };
      }),
    [stepsData]
  );

  const handlePickFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    const step = STEPS[activeStep];
    downloadTemplateXlsx(step.templateFileName, step.templateColumns);
  }, [activeStep]);

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      setStepsData((prev) => {
        const next = [...prev];
        next[activeStep] = {
          ...next[activeStep],
          loading: true,
          error: null,
        };
        return next;
      });

      try {
        const result = await parseExcelPreview(file);
        const headersValid = headersMatchTemplate(result.headers, meta.templateColumns);
        setStepsData((prev) => {
          const next = [...prev];
          const fileTag = `${result.fileName || "archivo"}-${Date.now()}`;
          const normalizedRows = (result.rows || []).map((row, index) => ({
            ...row,
            id: `${fileTag}-${index}`,
          }));
          const validationSummary =
            activeStep === 1
              ? normalizedRows.reduce(
                  (acc, row) => {
                    const validation = validarPrestamoRow(row);
                    if (validation.ok) acc.valid += 1;
                    else acc.invalid += 1;
                    return acc;
                  },
                  { valid: 0, invalid: 0 }
                )
              : null;
          next[activeStep] = {
            fileName: result.fileName,
            headers: result.headers,
            rows: normalizedRows,
            rowCount: result.rowCount,
            error: headersValid
              ? null
              : `Las columnas del archivo no coinciden con la plantilla de "${meta.label}". Deben ser exactamente: ${meta.templateColumns.join(", ")}.`,
            loading: false,
            status: headersValid ? "prevalidado" : "idle",
            saveSummary: null,
            validationSummary,
          };
          return next;
        });
        setPreviewVersion((v) => v + 1);
      } catch (err) {
        setStepsData((prev) => {
          const next = [...prev];
          next[activeStep] = {
            ...createEmptyStepState(),
            error: err?.message || "Error al leer el archivo.",
            loading: false,
          };
          return next;
        });
      }
    },
    [activeStep, meta]
  );

  const handleClearFile = useCallback(() => {
    setStepsData((prev) => {
      const next = [...prev];
      next[activeStep] = createEmptyStepState();
      return next;
    });
  }, [activeStep]);

  const handleBack = useCallback(() => {
    setActiveStep((s) => Math.max(0, s - 1));
  }, []);

  const handleNext = useCallback(() => {
    setActiveStep((s) => Math.min(STEPS.length - 1, s + 1));
  }, []);

  const handleProcessRowUpdate = useCallback(
    (newRow) => {
      setStepsData((prev) => {
        const next = [...prev];
        const step = next[activeStep];
        if (!step) return prev;

        const updatedRows = (step.rows || []).map((row) => (row.id === newRow.id ? newRow : row));
        next[activeStep] = {
          ...step,
          rows: updatedRows,
        };
        return next;
      });
      return newRow;
    },
    [activeStep]
  );

  const handleFinish = useCallback(() => {
    setFinishConfirmOpen(true);
  }, []);

  const handleGuardarProductosLote = useCallback(async () => {
    const productosRows = stepsData[0]?.rows ?? [];
    if (productosRows.length === 0) return;
    setSavingProductos(true);
    setStepsData((prev) => {
      const next = [...prev];
      next[0] = {
        ...next[0],
        error: null,
        status: "listo_para_guardar",
      };
      return next;
    });
    try {
      const { ok, data } = await post("/api/importacion/productos/lote", {
        productos: productosRows,
        allOrNothing: false,
      });
      if (!ok || data?.ok === false) {
        throw new Error(data?.message || data?.Error || "No se pudo guardar el lote de productos.");
      }
      const inserted = data?.data?.inserted ?? [];
      const errors = data?.data?.errors ?? [];
      const insertedIds = new Set(inserted.map((x) => x.index));

      setStepsData((prev) => {
        const next = [...prev];
        const step0 = next[0];
        const remainingRows = (step0.rows || []).filter((_, index) => !insertedIds.has(index));
        next[0] = {
          ...step0,
          rows: remainingRows,
          rowCount: remainingRows.length,
          status: inserted.length > 0 ? "guardado" : "prevalidado",
          saveSummary: {
            inserted: inserted.length,
            failed: errors.length,
            errors,
          },
          error: null,
        };
        return next;
      });
    } catch (e) {
      setStepsData((prev) => {
        const next = [...prev];
        next[0] = {
          ...next[0],
          status: "prevalidado",
          error: e?.message || "No se pudo guardar el lote de productos.",
        };
        return next;
      });
    } finally {
      setSavingProductos(false);
    }
  }, [stepsData]);

  const handleConfirmFinish = useCallback(() => {
    setActiveStep(0);
    setStepsData([createEmptyStepState(), createEmptyStepState()]);
    setSaveError("");
    setAmortModal({ open: false, error: null, filas: [], resumen: null });
    setSavingPrestamo(false);
    setProductoSeleccionado(null);
    setSucursalSeleccionada(null);
    setSucursalOptions([]);
    setPrestamoSeleccionadoId(null);
    setPrestamoSeleccionadoRow(null);
    setFinishConfirmOpen(false);
  }, []);

  const handleGridRowClick = useCallback((params) => {
    if (activeStep !== 1 || current.error) return;
    setPrestamoSeleccionadoId(params.row?.id ?? null);
    setPrestamoSeleccionadoRow(params.row ?? null);
    setDetalleCapital(getRowValueByNames(params.row, ["capital", "Capital"]));
    setDetalleInteres(getRowValueByNames(params.row, ["tasa", "Tasa", "interes", "Interes"]));
    setDetalleBalancePendiente(
      getRowValueByNames(params.row, ["balance pendiente", "balance_pendiente", "balancependiente"])
    );
    const result = generarTablaAmortizacionFlat(params.row);
    if (!result.ok) {
      setAmortModal({
        open: true,
        error: result.error,
        filas: [],
        resumen: null,
      });
      return;
    }
    setAmortModal({
      open: true,
      error: null,
      filas: result.filas,
      resumen: result.resumen,
    });
  }, [activeStep, current.error]);

  const handleCloseAmortModal = useCallback(() => {
    setAmortModal((prev) => ({ ...prev, open: false }));
    setSaveError("");
    setProductoSeleccionado(null);
    setSucursalSeleccionada(null);
    setPrestamoSeleccionadoId(null);
    setPrestamoSeleccionadoRow(null);
    setDetalleCapital("");
    setDetalleInteres("");
    setDetalleBalancePendiente("");
  }, []);

  useEffect(() => {
    if (!amortModal.open || prestamoSeleccionadoRow == null) return;
    const rowEditado = {
      ...prestamoSeleccionadoRow,
      capital: detalleCapital,
      Capital: detalleCapital,
      tasa: detalleInteres,
      Tasa: detalleInteres,
      "balance pendiente": detalleBalancePendiente,
      balance_pendiente: detalleBalancePendiente,
      balancependiente: detalleBalancePendiente,
    };
    const result = generarTablaAmortizacionFlat(rowEditado);
    if (!result.ok) {
      setAmortModal((prev) => ({
        ...prev,
        error: result.error,
        filas: [],
        resumen: null,
      }));
      return;
    }
    setPrestamoSeleccionadoRow(rowEditado);
    setAmortModal((prev) => ({
      ...prev,
      error: null,
      filas: result.filas,
      resumen: result.resumen,
    }));
  }, [amortModal.open, prestamoSeleccionadoId, detalleCapital, detalleInteres, detalleBalancePendiente]);

  useEffect(() => {
    if (!amortModal.open) return;
    setLoadingSucursales(true);
    setLoadingProductos(true);

    post("/api/recuperasucursales", { sucursal: " " })
      .then(({ data }) => {
        const list = Array.isArray(data?.sucursales) ? data.sucursales : [];
        setSucursalOptions(list);
        if (list.length > 0 && !sucursalSeleccionada) {
          setSucursalSeleccionada(list[0]);
        }
      })
      .catch(() => setSucursalOptions([]))
      .finally(() => setLoadingSucursales(false));

    post("/api/importacion/productos/disponibles", {})
      .then(({ data }) => {
        const list = Array.isArray(data?.data?.productos) ? data.data.productos : [];
        
        if (list.length > 0) {
          
          setProductoOptions(list);
          return;
        }
        setProductoOptions(productosPaso1);
      })
      .catch(() => setProductoOptions(productosPaso1))
      .finally(() => setLoadingProductos(false));
  }, [amortModal.open, productosPaso1]);

  const handleGuardarPrestamo = useCallback(async () => {
    if (productoSeleccionado == null || prestamoSeleccionadoId == null || prestamoSeleccionadoRow == null) return;

    setSaveError("");
    setSavingPrestamo(true);

    const sucursalCodigo = (() => {
      const raw =
        typeof sucursalSeleccionada === "string"
          ? sucursalSeleccionada
          : sucursalSeleccionada?.label || sucursalSeleccionada?.descripcion || "";
      return String(raw).split("-")[0].trim();
    })();

    const body = {
      prestamo: prestamoSeleccionadoRow,
      producto: productoSeleccionado.row,
      sucursal: sucursalCodigo,
      amortizacion: amortModal.filas,
      interesPagado: amortModal.resumen.interesPagado,
      capitalPagado: amortModal.resumen.capitalPagado,
      mora: prestamoSeleccionadoRow.mora,


    };

    try {
      const { ok, data } = await post("/api/importacion/prestamos/guardar", body);
      if (!ok) {
        throw new Error(data?.message || data?.Error || "No se pudo importar el préstamo.");
      }

      setStepsData((prev) => {
        const next = [...prev];

        const productos = next[0] ?? createEmptyStepState();
        const prestamos = next[1] ?? createEmptyStepState();

        const productosRows = productos.rows.filter((r) => r.id !== productoSeleccionado.id);
        const prestamosRows = prestamos.rows.filter((r) => r.id !== prestamoSeleccionadoId);

        next[0] = {
          ...productos,
          rows: productosRows,
          rowCount: Math.max(0, (productos.rowCount ?? productos.rows.length) - 1),
        };

        next[1] = {
          ...prestamos,
          rows: prestamosRows,
          rowCount: Math.max(0, (prestamos.rowCount ?? prestamos.rows.length) - 1),
          status: "guardado",
        };

        return next;
      });

      handleCloseAmortModal();
    } catch (e) {
      setSaveError(e?.message || "No se pudo guardar el préstamo.");
    } finally {
      setSavingPrestamo(false);
    }

  }, [
    productoSeleccionado,
    sucursalSeleccionada,
    prestamoSeleccionadoId,
    prestamoSeleccionadoRow,
    amortModal.filas,
    handleCloseAmortModal,
  ]);

  const isPrestamosStep = activeStep === 1;
  const productosState = stepsData[0];

  return (
    <Box component="main" sx={{ minHeight: '100%', bgcolor: 'grey.50', py: 3, px: { xs: 2, sm: 3 } }}>
      <MDBox py={0}>
      <Box component="main" sx={{ px: 0, pb: 0 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{ display: "none" }}
          aria-label={`Seleccionar archivo Excel para ${meta.label}`}
          onChange={handleFileChange}
        />

        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "background.paper" }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((s) => (
              <Step key={s.key}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" component="h1" gutterBottom>
              Paso {activeStep + 1} de {STEPS.length}: {meta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {meta.description}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {meta.hint}
            </Alert>
            {current.status !== "idle" && (
              <Alert severity={current.status === "guardado" ? "success" : "info"} sx={{ mb: 2 }}>
                Estado actual: <strong>{current.status}</strong>
                {current.validationSummary && (
                  <>
                    {" "}
                    | Válidos: {current.validationSummary.valid} | Inválidos:{" "}
                    {current.validationSummary.invalid}
                  </>
                )}
              </Alert>
            )}
            {activeStep === 0 && current.saveSummary && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Productos guardados: {current.saveSummary.inserted}. Errores: {current.saveSummary.failed}.
                {Array.isArray(current.saveSummary.errors) && current.saveSummary.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {current.saveSummary.errors.map((err, idx) => {
                      const fila = Number.isFinite(err?.index) ? err.index + 1 : "?";
                      const campo = err?.field ? ` | Campo: ${err.field}` : "";
                      const mensaje = err?.message || "Error no especificado.";
                      return (
                        <Typography key={`prod-error-${idx}`} variant="body2" sx={{ display: "block" }}>
                          Fila {fila}
                          {campo}: {mensaje}
                        </Typography>
                      );
                    })}
                  </Box>
                )}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <MDButton
                variant="outlined"
                color="dark"
                onClick={handleDownloadTemplate}
                startIcon={<DownloadOutlinedIcon />}
                aria-label={`Descargar plantilla Excel para ${meta.label}`}
              >
                Descargar plantilla
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handlePickFile}
                disabled={current.loading}
                aria-label={`Elegir archivo Excel para ${meta.label}`}
              >
                {current.fileName ? "Cambiar archivo" : "Elegir archivo Excel"}
              </MDButton>
              {current.fileName && (
                <MDButton variant="outlined" color="dark" onClick={handleClearFile} disabled={current.loading}>
                  Quitar archivo
                </MDButton>
              )}
              {current.fileName && (
                <Chip label={current.fileName} variant="outlined" size="small" />
              )}
              {current.rowCount > 0 && (
                <Chip
                  label={`${current.rowCount} fila${current.rowCount === 1 ? "" : "s"} de datos`}
                  color="primary"
                  size="small"
                />
              )}
              {current.loading && <CircularProgress size={22} aria-label="Leyendo archivo" />}
            </Stack>
            {current.loading && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress />
              </Box>
            )}
            {current.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {current.error}
              </Alert>
            )}
            {isPrestamosStep && current.rows.length > 0 && !current.loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Pulse una fila del préstamo para abrir la tabla de amortización (método flat).
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <MDBox
              sx={{
                height: 380,
                width: "100%",
                bgcolor: "background.paper",
                borderRadius: 2,
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
              }}
            >
              {gridColumns.length > 0 ? (
                <DataGrid
                  key={`${activeStep}-${previewVersion}`}
                  rows={current.rows}
                  columns={gridColumns.map((col) => ({ ...col, editable: true }))}
                  onRowClick={isPrestamosStep ? handleGridRowClick : undefined}
                  processRowUpdate={handleProcessRowUpdate}
                  onProcessRowUpdateError={(error) => {
                    setStepsData((prev) => {
                      const next = [...prev];
                      next[activeStep] = {
                        ...next[activeStep],
                        error: error?.message || "No se pudo actualizar la fila editada.",
                      };
                      return next;
                    });
                  }}
                  disableRowSelectionOnClick
                  hideFooter
                  density="compact"
                  disableVirtualization
                />
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: "100%", px: 2 }}
                  spacing={1}
                >
                  <Typography color="text.secondary" variant="body2" align="center">
                    {current.loading
                      ? "Leyendo archivo…"
                      : "La vista previa de las primeras filas aparecerá aquí después de elegir un archivo."}
                  </Typography>
                </Stack>
              )}
            </MDBox>
            {current.rowCount > 50 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Vista previa: se muestran como máximo 50 filas. Total en archivo: {current.rowCount}.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
              <MDButton variant="outlined" color="dark" onClick={handleBack} disabled={activeStep === 0}>
                Anterior
              </MDButton>
              {activeStep === 0 && (
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleGuardarProductosLote}
                  disabled={savingProductos || current.rows.length === 0 || Boolean(current.error)}
                >
                  {savingProductos ? "Guardando productos..." : "Guardar productos"}
                </MDButton>
              )}
              {activeStep < STEPS.length - 1 ? (
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleNext}
                >
                  Siguiente
                </MDButton>
              ) : (
                <MDButton variant="gradient" color="error" onClick={handleFinish}>
                  Finalizar importación
                </MDButton>
              )}
            </Stack>
          </Grid>
        </Grid>

        <Dialog
          open={amortModal.open}
          onClose={handleCloseAmortModal}
          maxWidth="xl"
          fullWidth
          scroll="paper"
          aria-labelledby="amortizacion-dialog-title"
        >
          <DialogTitle id="amortizacion-dialog-title" sx={{ pr: 6 }}>
            Detalles
            <IconButton
              aria-label="Cerrar"
              onClick={handleCloseAmortModal}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ width: "100%", boxSizing: "border-box", minWidth: 0 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Capital"
                  value={detalleCapital}
                  onChange={(e) => setDetalleCapital(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Interés (tasa)"
                  value={detalleInteres}
                  onChange={(e) => setDetalleInteres(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Balance pendiente"
                  value={detalleBalancePendiente}
                  onChange={(e) => setDetalleBalancePendiente(e.target.value)}
                />
              </Grid>
            </Grid>
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                value={productoSeleccionado}
                onChange={(_, value) => setProductoSeleccionado(value)}
                options={productoOptions}
                loading={loadingProductos}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No hay productos disponibles (estado distinto de V)"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Producto disponible"
                    placeholder="Seleccione un producto"
                  />
                )}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                value={sucursalSeleccionada}
                onChange={(_, value) => setSucursalSeleccionada(value)}
                options={sucursalOptions}
                loading={loadingSucursales}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option?.label || option?.descripcion || ""
                }
                isOptionEqualToValue={(option, value) =>
                  (option?.id ?? option?.label ?? option) === (value?.id ?? value?.label ?? value)
                }
                noOptionsText="No hay sucursales disponibles"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sucursal"
                    placeholder="Seleccione una sucursal"
                  />
                )}
              />
            </Box>
            {amortModal.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {amortModal.error}
              </Alert>
            )}
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}
            {amortModal.resumen && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                </Typography>
                {(amortModal.resumen.capitalPagadoImporte > 0 ||
                  amortModal.resumen.interesPagadoImporte > 0) && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Importes importados (capital pagado:{" "}
                    <strong>{formatearMoneda(amortModal.resumen.capitalPagadoImporte)}</strong>, interés
                    pagado: <strong>{formatearMoneda(amortModal.resumen.interesPagadoImporte)}</strong>)
                    repartidos en partes iguales entre las {amortModal.resumen.plazo} cuotas en las columnas
                    «Capital pagado» e «Interés pagado».
                  </Typography>
                )}
              </Box>
            )}
            {amortModal.filas.length > 0 && (
              <Box sx={{ width: "100%", minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  {amortModal.filas.length} cuota{amortModal.filas.length === 1 ? "" : "s"}
                  {amortModal.resumen?.plazo != null && amortModal.filas.length !== amortModal.resumen.plazo
                    ? ` (plazo: ${amortModal.resumen.plazo})`
                    : ""}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    maxHeight: 420,
                    overflow: "auto",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      ...amortTableRowSx,
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      borderBottom: 1,
                      borderColor: "divider",
                      fontWeight: 600,
                    }}
                  >
                    <Box sx={amortTableCellSx}>#</Box>
                    <Box sx={amortTableCellSx}>Fecha</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Cuota</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Capital</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Interés</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Capital pagado</Box>
                    <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>Interés pagado</Box>
                    <Box sx={amortTableCellSx}>Estado</Box>
                  </Box>
                  {amortModal.filas.map((f) => (
                    <Box
                      key={f.periodo}
                      sx={{
                        ...amortTableRowSx,
                        borderBottom: 1,
                        borderColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                        "&:last-child": { borderBottom: 0 },
                      }}
                    >
                      <Box sx={amortTableCellSx}>{f.periodo}</Box>
                      <Box sx={amortTableCellSx}>{formatearFecha(f.fecha)}</Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.cuota)}
                      </Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.capital)}
                      </Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.interes)}
                      </Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.capitalPagado)}
                      </Box>
                      <Box sx={{ ...amortTableCellSx, textAlign: "right" }}>
                        {formatearMoneda(f.interesPagado)}
                      </Box>
                      <Box sx={amortTableCellSx}>
                        {f.estado === "pagada"
                          ? "Pagada"
                          : f.estado === "parcial"
                            ? "Parcial"
                            : "Pendiente"}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleGuardarPrestamo}
              disabled={
                savingPrestamo ||
                productoSeleccionado == null ||
                sucursalSeleccionada == null ||
                prestamoSeleccionadoId == null
              }
            >
              {savingPrestamo ? "Guardando..." : "Guardar préstamo"}
            </MDButton>
            <MDButton variant="outlined" color="dark" onClick={handleCloseAmortModal}>
              Cerrar
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={finishConfirmOpen}
          onClose={() => setFinishConfirmOpen(false)}
          aria-labelledby="confirmar-finalizar-importacion-title"
        >
          <DialogTitle id="confirmar-finalizar-importacion-title">
            ¿Desea finalizar la importación?
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary">
              Al finalizar se limpiarán los datos cargados y el proceso iniciará desde el paso 1.
            </Typography>
          </DialogContent>
          <DialogActions>
            <MDButton variant="outlined" color="dark" onClick={() => setFinishConfirmOpen(false)}>
              Cancelar
            </MDButton>
            <MDButton variant="gradient" color="error" onClick={handleConfirmFinish}>
              Finalizar importación
            </MDButton>
          </DialogActions>
        </Dialog>
      </Box>
      </MDBox>
    </Box>
  );
}

export default ImportacionDatos;
